import { FC, useState } from 'react';

import { Image } from '@shared/api/media/media.interface';
import { Loading } from '@shared/components/Loading';
import { Link } from 'react-router-dom';

import { DeliverModal } from '../components/DeliverModal';
import { PhoneHistoryModal } from '../components/PhoneHistoryModal';
import {
  RedemptionCouponTable,
  VerificationData,
} from '../components/RedemptionCouponTable';
import { RedemptionStatsBar } from '../components/RedemptionStatsBar';
import { ShareHistoryModal } from '../components/ShareHistoryModal';
import { SignatureViewModal } from '../components/SignatureViewModal';
import { TodayDeliveredTable } from '../components/TodayDeliveredTable';
import { VerificationViewModal } from '../components/VerificationViewModal';
import { useRedemptionPage } from '../hooks/useRedemptionPage';

export const GiftCouponRedemptionListPage: FC = function GiftCouponRedemptionListPage() {
  const {
    dashboardStatus,
    statistics,
    visibleRequested,
    todayDelivered,
    filteredData,
    productOptions,
    productFilter,
    setProductFilter,
    searchText,
    setSearchText,
    selectedIds,
    isAllSelected,
    handleToggleAll,
    handleToggleSelect,
    setSelectedIds,
    overIssuedSelectedIds,
    deselectOverIssued,
    deliveringId,
    isBulkProcessing,
    openDeliverModal,
    openBulkDeliverModal,
    deliverModal,
    setDeliverModal,
    handleConfirmDeliver,
    handleReject,
    handleUpdateRecipientPhone,
    historyModal,
    setHistoryModal,
    openHistoryModal,
    shareHistoryModal,
    setShareHistoryModal,
    openShareHistoryModal,
    showTodayDelivered,
    setShowTodayDelivered,
    handleBulkUpdateDeliveredCoupon,
    toastMessage,
    showToast,
    copyToClipboard,
  } = useRedemptionPage();

  const [signatureModalImage, setSignatureModalImage] = useState<Image | null>(null);
  const [verificationModal, setVerificationModal] = useState<VerificationData | null>(null);

  if (dashboardStatus === 'loading' || dashboardStatus === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-6 py-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">발송 대기 쿠폰</h1>
            <p className="mt-1 text-sm text-gray-500">
              교환 신청된 쿠폰을 확인하고 기프티콘을 발송하세요.
            </p>
          </div>
          <Link
            to="/gift-seller/coupon-history"
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            쿠폰 내역 검색
          </Link>
        </div>
      </div>

      {/* Stats */}
      <RedemptionStatsBar
        totalPending={visibleRequested.length}
        todayDeliveredCount={todayDelivered.length}
        productStats={productOptions}
        blacklist={statistics?.blacklist ?? []}
        onProductClick={(key) => setProductFilter(key)}
      />

      {/* 오늘 발송 내역 */}
      <TodayDeliveredTable
        todayDelivered={todayDelivered}
        showTodayDelivered={showTodayDelivered}
        setShowTodayDelivered={setShowTodayDelivered}
        copyToClipboard={copyToClipboard}
        showToast={showToast}
        onBulkUpdate={handleBulkUpdateDeliveredCoupon}
      />

      {/* Filters */}
      <div className="bg-white px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={productFilter}
            onChange={(e) => setProductFilter(e.target.value)}
            className="max-w-[260px] rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상품</option>
            {productOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label} ({opt.count})
              </option>
            ))}
          </select>

          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="쿠폰번호, 전화번호, 주문자번호, 상품명 검색"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        {selectedIds.size > 0 && (
          <div
            className={`mt-3 flex flex-wrap items-center gap-3 rounded-lg px-4 py-3 ${
              overIssuedSelectedIds.size > 0
                ? 'bg-red-50 ring-1 ring-red-300'
                : 'bg-indigo-50'
            }`}
          >
            <span
              className={`text-sm font-medium ${
                overIssuedSelectedIds.size > 0 ? 'text-red-800' : 'text-indigo-700'
              }`}
            >
              {selectedIds.size}건 선택됨
            </span>

            {overIssuedSelectedIds.size > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1 text-xs font-bold text-white shadow-sm ring-2 ring-red-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                초과발송건이 {overIssuedSelectedIds.size}건 확인됨
              </span>
            )}

            <button
              onClick={openBulkDeliverModal}
              disabled={isBulkProcessing || overIssuedSelectedIds.size > 0}
              title={
                overIssuedSelectedIds.size > 0
                  ? '초과발송 의심 건이 포함되어 일괄 발송할 수 없습니다.'
                  : undefined
              }
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                overIssuedSelectedIds.size > 0
                  ? 'bg-red-400 hover:bg-red-400'
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {isBulkProcessing
                ? '처리 중...'
                : overIssuedSelectedIds.size > 0
                ? '일괄 발송 불가'
                : '일괄 발송처리'}
            </button>

            {overIssuedSelectedIds.size > 0 && (
              <button
                onClick={deselectOverIssued}
                className="rounded-lg border border-red-400 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                초과건 {overIssuedSelectedIds.size}개 선택 해제
              </button>
            )}

            <button
              onClick={() => setSelectedIds(new Set())}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              전체 선택 해제
            </button>

            {overIssuedSelectedIds.size > 0 && (
              <p className="basis-full text-xs font-medium text-red-700">
                ! 구매 수량을 초과한 위험 쿠폰이 선택되어 있습니다. 해당 건을 선택
                해제해야 일괄 발송이 가능합니다.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="p-4">
        <RedemptionCouponTable
          filteredData={filteredData}
          selectedIds={selectedIds}
          deliveringId={deliveringId}
          productFilter={productFilter}
          onToggleAll={handleToggleAll}
          onToggleSelect={handleToggleSelect}
          onOpenDeliverModal={openDeliverModal}
          onOpenHistoryModal={openHistoryModal}
          onOpenShareHistoryModal={openShareHistoryModal}
          onOpenSignatureModal={setSignatureModalImage}
          onOpenVerificationModal={setVerificationModal}
          onUpdateRecipientPhone={handleUpdateRecipientPhone}
          isAllSelected={isAllSelected}
          copyToClipboard={copyToClipboard}
          showToast={showToast}
        />
      </div>

      {/* Modals */}
      {deliverModal && (
        <DeliverModal
          deliverModal={deliverModal}
          setDeliverModal={setDeliverModal}
          onConfirm={handleConfirmDeliver}
          onReject={handleReject}
        />
      )}

      {historyModal && (
        <PhoneHistoryModal
          phone={historyModal.phone}
          history={historyModal.history}
          onClose={() => setHistoryModal(null)}
        />
      )}

      {shareHistoryModal && (
        <ShareHistoryModal
          fullCode={shareHistoryModal.fullCode}
          history={shareHistoryModal.history}
          onClose={() => setShareHistoryModal(null)}
        />
      )}

      {signatureModalImage && (
        <SignatureViewModal
          signatureImage={signatureModalImage}
          onClose={() => setSignatureModalImage(null)}
        />
      )}

      {verificationModal && (
        <VerificationViewModal
          verification={verificationModal}
          onClose={() => setVerificationModal(null)}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <div className="pointer-events-none fixed inset-x-0 bottom-8 z-[100] flex justify-center">
          <div className="animate-fade-in-out rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {toastMessage}
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          15% { opacity: 1; transform: translateY(0); }
          75% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 1.2s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
};
